import type { StateCreator } from 'zustand';
import type { StoreState } from '../types/types';
import type { Combatant, PartyMember } from '../types/types';
import { api } from '../services/api';
import { logger } from '../services/logger.client';

// NOTE: Combat state persistence is now handled by the useCombatSync hook (ADR-033).
// This hook observes Zustand state changes and syncs to the backend with 500ms debounce.
// Action functions in this file should only mutate state - the hook handles all persistence.

export interface CombatState {
    isActive: boolean;
    setupMode: boolean;
    combatants: Combatant[];
    turn: number;
    round: number;
}
export interface CombatActions {
    setupCombat: () => Promise<void>;
    beginCombat: () => void;
    endCombat: () => Promise<void>;
    nextTurn: () => void;
    addCombatant: (combatant: Omit<Combatant, 'id'> & { id?: number }) => void;
    removeCombatant: (id: number) => void;
    updateCombatant: (combatant: Partial<Combatant> & { id: number }) => void;
    updateCombatantOrder: (ids: number[]) => void;
    addStatusEffect: (combatantId: number, status: string) => void;
    removeStatusEffect: (combatantId: number, status: string) => void;
    rollAllInitiatives: () => void;
    longRest: () => void;
}

export type CombatSlice = CombatState & CombatActions;

export const createCombatActions: StateCreator<StoreState, [['zustand/immer', never]], [], { state: CombatState, actions: CombatActions }> = (set, get) => ({
    state: { isActive: false, setupMode: false, combatants: [], turn: 0, round: 1 },
    actions: {
        setupCombat: async () => {
            const { partyRoster } = get().session.roster;
            const combatants = (partyRoster ?? []).map((p: PartyMember) => ({
                ...p, id: p.id,
                creatureId: p.id,
                creatureType: 'player' as const,
                initiative: 0,
                statusEffects: [],
            }));

            // Immediately enter setup mode for responsive UI.
            // This ensures the UI shows setup mode buttons while the API call is in progress.
            set(store => {
                store.session.combat = {
                    ...store.session.combat,
                    combatants: combatants as Combatant[],
                    setupMode: true,
                    isActive: false,
                    round: 1,
                    turn: 0
                };
            });

            // Make the API call to persist the combat state.
            // If successful, update with server-assigned IDs.
            // If it fails, the UI is still in setup mode so the user can retry or continue.
            try {
                const response = await api('/api/combat/start', { method: 'POST', body: JSON.stringify(combatants) });
                const newCombatants = await response.json() as Combatant[];
                set(store => {
                    store.session.combat.combatants = newCombatants;
                });
            } catch (error) {
                logger.error(error, 'Error setting up combat - combatants may not be persisted');
                // Note: We don't revert setupMode on error. The user can still use the combat tracker
                // locally even if the server sync fails.
            }
        },
        beginCombat: () => {
            // Sort combatants by initiative and activate combat.
            // Persistence is handled automatically by useCombatSync hook (ADR-033).
            set(store => {
                (store.session.combat.combatants ?? []).sort((a: Combatant, b: Combatant) => b.initiative - a.initiative);
                store.session.combat.isActive = true;
                store.session.combat.setupMode = false;
                store.session.combat.turn = 0;
                store.session.combat.round = 1;
            });
            get().actions.addLogEntry('Combat has begun!');
        },
        endCombat: async () => {
            try {
                await api('/api/combat/end', { method: 'DELETE' });
                set(store => {
                    store.session.combat = { isActive: false, setupMode: false, combatants: [], turn: 0, round: 1 };
                });
                get().actions.addLogEntry('Combat has ended.');
            } catch (error) {
                logger.error(error, 'Error ending combat');
            }
        },
        nextTurn: () => {
            // Advance to the next combatant's turn, incrementing round if necessary.
            // Persistence is handled automatically by useCombatSync hook (ADR-033).
            const numCombatants = get().session.combat.combatants.length;
            if (numCombatants === 0) return;

            set(store => {
                const newTurn = (store.session.combat.turn + 1) % numCombatants;
                store.session.combat.turn = newTurn;
                if (newTurn === 0) {
                    store.session.combat.round += 1;
                    get().actions.addLogEntry(`Round ${store.session.combat.round} begins.`);
                }
            });
        },
        addCombatant: (combatant) => set(store => {
            // This action is local-only and doesn't need an API call or error handling in the same way.
            // The modal will handle its own state.
            const newCombatant = {
                ...combatant,
                id: combatant.id ?? -Date.now(),
                hp: combatant.hp ?? 10,
                maxHp: combatant.maxHp ?? combatant.hp ?? 10,
                ac: combatant.ac ?? 10,
                dexMod: combatant.dexMod ?? 0,
                initiative: combatant.initiative ?? 0,
                statusEffects: combatant.statusEffects ?? [],
                color: combatant.color ?? '#808080',
            } as Combatant;
            store.session.combat.combatants.push(newCombatant);
        }),
        removeCombatant: (id: number) => set(store => {
            store.session.combat.combatants = (store.session.combat.combatants ?? []).filter((c: Combatant) => c.id !== id);
        }),
        updateCombatant: (combatant: Partial<Combatant> & { id: number }) => {
            // Update a single combatant's properties.
            // Persistence is handled automatically by useCombatSync hook (ADR-033).
            set(store => {
                const index = (store.session.combat.combatants ?? []).findIndex((c: Combatant) => c.id === combatant.id);
                if (index !== -1) {
                    // Filter out undefined values from the payload to prevent type errors with Immer.
                    const updatePayload = Object.fromEntries(
                        Object.entries(combatant).filter(([, value]) => value !== undefined)
                    );
                    store.session.combat.combatants[index] = { ...store.session.combat.combatants[index]!, ...updatePayload };
                }
            });
        },
        updateCombatantOrder: (ids: number[]) => {
            // Reorder combatants based on the provided ID sequence.
            // Persistence is handled automatically by useCombatSync hook (ADR-033).
            set(store => {
                const combatantMap = new Map((store.session.combat.combatants ?? []).map((c: Combatant) => [c.id, c]));
                store.session.combat.combatants = (ids ?? []).map((id: number) => combatantMap.get(id)).filter((c: Combatant | undefined): c is Combatant => !!c);
            });
        },
        addStatusEffect: (combatantId: number, status: string) => {
            // Add a status effect to a combatant (if not already present).
            // Persistence is handled automatically by useCombatSync hook (ADR-033).
            set(store => {
                const combatant = (store.session.combat.combatants ?? []).find((c: Combatant) => c.id === combatantId);
                if (combatant && !combatant.statusEffects.includes(status)) {
                    combatant.statusEffects = [...combatant.statusEffects, status];
                }
            });
        },
        removeStatusEffect: (combatantId: number, status: string) => {
            // Remove a status effect from a combatant.
            // Persistence is handled automatically by useCombatSync hook (ADR-033).
            set(store => {
                const combatant = (store.session.combat.combatants ?? []).find((c: Combatant) => c.id === combatantId);
                if (combatant) {
                    combatant.statusEffects = combatant.statusEffects.filter((s: string) => s !== status);
                }
            });
        },
        rollAllInitiatives: () => {
            // Roll initiative for all monsters and NPCs.
            // Persistence is handled automatically by useCombatSync hook (ADR-033).
            set(store => {
                (store.session.combat.combatants ?? []).forEach((c: Combatant) => {
                    if (c.creatureType === 'monster' || c.creatureType === 'npc') {
                        c.initiative = Math.floor(Math.random() * 20) + 1 + (c.dexMod ?? 0);
                    }
                });
            });
        },
        longRest: () => set(store => {
            (store.session.roster.partyRoster ?? []).forEach((p: PartyMember) => {
                p.hp = p.maxHp;
            });
            get().actions.addLogEntry('The party takes a long rest.');
        }),
    }
});